import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vacanciesApi } from '../services/vacancies';
import { logout } from './authSlice';

export const fetchVacancies = createAsyncThunk(
    'vacancies/fetchList',
    async ({ skip = 0, limit = 20, org_id, filters = {} }, { rejectWithValue }) => {
        try {
            console.log('org_id', org_id, 'filters', filters);
            if (org_id === "all") {
                return await vacanciesApi.getList(skip, limit, org_id, filters);
            }
            return await vacanciesApi.getList(skip, limit, org_id, filters);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacancyById = createAsyncThunk(
    'vacancies/fetchById',
    async ({ id, orgId }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.getById(id, orgId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const createVacancy = createAsyncThunk(
    'vacancies/create',
    async ({ organisationId, vacancy }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.create(organisationId, vacancy);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateVacancy = createAsyncThunk(
    'vacancies/update',
    async ({ id, organisationId, vacancy }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.update(id, organisationId, vacancy);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteVacancy = createAsyncThunk(
    'vacancies/delete',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.delete(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const closeVacancy = createAsyncThunk(
    'vacancies/close',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.close(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateVacancyStatus = createAsyncThunk(
    'vacancies/updateStatus',
    async ({ id, organisationId, status }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.update(id, organisationId, { status });
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacanciesFunnelStatuses = createAsyncThunk(
    'vacancies/fetchFunnelStatuses',
    async (_, { rejectWithValue }) => {
        try {
            return await vacanciesApi.getVacanciesFunnelStatuses();
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacancyFunnelStatusesById = createAsyncThunk(
    'vacancies/fetchFunnelStatusesById',
    async (id, { rejectWithValue }) => {
        try {
            return await vacanciesApi.getVacancyFunnelStatusesById(id);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchVacancyHistory = createAsyncThunk(
    'vacancies/fetchHistory',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.getHistory(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const addVacancyFunnelStatus = createAsyncThunk(
    'vacancies/addFunnelStatus',
    async ({ id, funnelStatus }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.addVacancyFunnelStatus(id, funnelStatus);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateVacancyFunnelStatus = createAsyncThunk(
    'vacancies/updateFunnelStatus',
    async ({ id, funnelStatus }, { rejectWithValue }) => {
        try {
            return await vacanciesApi.updateVacancyFunnelStatus(id, funnelStatus);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const initialState = {
    list: [],
    funnelStatuses: [],
    vacancyFunnelStatuses: [],
    current: null,
    history: [],
    isLoading: false,
    error: null,
    totalPages: null, // Общее количество страниц для пагинации
    totalItems: null, // Общее количество записей
    showAllVacancies: JSON.parse(localStorage.getItem('showAllVacancies') || 'false') // Состояние для кнопки "Показать все вакансии"
};

const vacanciesSlice = createSlice({
    name: 'vacancies',
    initialState,
    reducers: {
        clearCurrent: (state) => {
            state.current = null;
            state.error = null;
        },
        updateVacancyStatusOptimistic: (state, action) => {
            const { id, status } = action.payload;
            const vacancy = state.list.find(v => v.id === id);
            if (vacancy) {
                vacancy.status = status;
            }
        },
        revertVacancyStatus: (state, action) => {
            const { id, originalStatus } = action.payload;
            const vacancy = state.list.find(v => v.id === id);
            if (vacancy) {
                vacancy.status = originalStatus;
            }
        },
        // Переключение состояния "Показать все вакансии"
        toggleShowAllVacancies: (state) => {
            state.showAllVacancies = !state.showAllVacancies;
        },
        // Установка состояния "Показать все вакансии"
        setShowAllVacancies: (state, action) => {
            state.showAllVacancies = action.payload;
            // Сохраняем в localStorage
            localStorage.setItem('showAllVacancies', JSON.stringify(action.payload));
        }
    },
    extraReducers: (builder) => {
        builder

            // Get List
            .addCase(fetchVacancies.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacancies.fulfilled, (state, action) => {
                state.isLoading = false;
                // action.payload теперь содержит { items, total_pages, total_items }
                state.list = action.payload.items || action.payload;
                state.totalPages = action.payload.total_pages || null;
                state.totalItems = action.payload.total_items || null;
            })
            .addCase(fetchVacancies.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get by ID
            .addCase(fetchVacancyById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacancyById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.current = action.payload.data;
            })
            .addCase(fetchVacancyById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Update
            .addCase(updateVacancy.fulfilled, (state, action) => {
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) state.list[index] = action.payload;
            })

            // Delete
            .addCase(deleteVacancy.fulfilled, (state, action) => {
                state.list = state.list.filter(item => item.id !== action.meta.arg.id);
            })

            // Close
            .addCase(closeVacancy.fulfilled, (state, action) => {
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) state.list[index] = action.payload;
            })

            // Get Funnel Statuses by ID
            .addCase(fetchVacancyFunnelStatusesById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchVacancyFunnelStatusesById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.vacancyFunnelStatuses = action.payload;
            })
            .addCase(fetchVacancyFunnelStatusesById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get Funnel Statuses
            .addCase(fetchVacanciesFunnelStatuses.pending, (state) => {
                // state.isLoading = true;
            })
            .addCase(fetchVacanciesFunnelStatuses.fulfilled, (state, action) => {
                state.isLoading = false;
                state.funnelStatuses = action.payload;
            })
            .addCase(fetchVacanciesFunnelStatuses.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get History
            .addCase(fetchVacancyHistory.pending, (state) => {
                // state.isLoading = true;
            })
            .addCase(fetchVacancyHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.history = action.payload;
            })
            .addCase(fetchVacancyHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Обработка logout из authSlice
            .addCase(logout, (state) => {
                state.list = [];
                state.funnelStatuses = [];
                state.vacancyFunnelStatuses = [];
                state.current = null;
                state.history = [];
                state.isLoading = false;
                state.loading = false;
                state.error = null;
                state.showAllVacancies = false;
            })
    }
});

export const {
    clearCurrent,
    updateVacancyStatusOptimistic,
    revertVacancyStatus,
    toggleShowAllVacancies,
    setShowAllVacancies
} = vacanciesSlice.actions;
export const vacanciesReducer = vacanciesSlice.reducer; 